import { Component, EventEmitter, Input, OnChanges, OnInit, Output, inject, viewChildren, viewChild, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Advertiser, CalculationBasedType, Campaign, CampaignTargetUser, CoinReward, ConsumptionLimitRule, IStampReward, ProductReward, PunchCardReceiptToolType, PunchCardSchema, PunchCardStrategy, PunchCardStrategyType, RewardData, StampReward, TimeLimitRule } from 'src/app/api/api-campaigns.service.generated';
import { MessageDialogModel } from 'src/app/common/message-dialog/message-dialog.component';
import { DateUtils } from 'src/app/common/utils/DateUtils';
import { GUID } from 'src/app/common/utils/GuidUtils';
import { RewardSelectComponent } from '../../_/reward-select/reward-select.component';
import { PunchCardCampaignService } from '../punch-card-campaign-edit.service';
import { PunchCardSchemeComponent } from '../punch-card-scheme/punch-card-scheme.component';
import { Currencies } from './lib/currencies';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { ButtonComponent } from 'wecheer-common';
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { FlexLayoutModule } from '@angular/flex-layout';


@Component({
  selector: 'app-punch-card-strategy',
  templateUrl: './punch-card-strategy.component.html',
  styleUrls: ['./punch-card-strategy.component.scss'],
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatExpansionModule,
    FormsModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    RewardSelectComponent,
    PunchCardSchemeComponent,
    ButtonComponent
  ]
})
export class PunchCardStrategyComponent implements OnInit, OnChanges {
  punchCardCampaignService = inject(PunchCardCampaignService);
  dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);


  readonly schemeComponentList = viewChildren(PunchCardSchemeComponent);

  @Input() campaign: Campaign;
  @Input() strategyType: PunchCardStrategyType | undefined;
  @Input() captureToolType: string;
  @Input() baseCalculation: CalculationBasedType;
  @Input() currencyCode: string;

  @Output() onStrategyTypeChanged: EventEmitter<any> = new EventEmitter();
  @Output() onBaseCalculationChanged: EventEmitter<any> = new EventEmitter();
  @Output() onCurrencyChanged: EventEmitter<any> = new EventEmitter();
  @Output() onSchemeChanged: EventEmitter<any> = new EventEmitter();

  readonly joinReward = viewChild<RewardSelectComponent>('joinReward');
  readonly firstConsumptionReward = viewChild<RewardSelectComponent>('firstConsumptionReward');
  readonly consumptionReward = viewChild<RewardSelectComponent>('consumptionReward');
  readonly birthdayReward = viewChild<RewardSelectComponent>('birthdayReward');

  advertiser: Advertiser;
  campaigns: Campaign[];
  rewardProductsOptions: ProductReward[] = [];

  strategy: PunchCardStrategy;
  advertiserId: string;
  campaignId: string;

  isLoading = false;
  panelOpenState = false;
  showIncentive = false;
  private _isValid: boolean = true;
  strategyTypeList = [
    { label: PunchCardStrategyType.Stamps, value: PunchCardStrategyType.Stamps },
    { label: PunchCardStrategyType.Points, value: PunchCardStrategyType.Points }
  ];

  baseCalculationList = [
    { label: CalculationBasedType.QuantityBased, value: CalculationBasedType.QuantityBased },
    { label: CalculationBasedType.ValueBased, value: CalculationBasedType.ValueBased }
  ];

  currencyList = Currencies;
  receiptToolTypes = PunchCardReceiptToolType;
  schemeList: PunchCardSchema[] = [];
  get captureToolTypes(): any[] {
    let res = this.strategyTypeList;
    const effectiveCaptureToolType = this.captureToolType ?? this.punchCardCampaignService.captureToolType;
    const isStaffNonAdvertiser = this.punchCardCampaignService.targetRole === CampaignTargetUser.Staff
      && effectiveCaptureToolType !== PunchCardReceiptToolType.AdvertiserCampaign;
    if (effectiveCaptureToolType == PunchCardReceiptToolType.External.toString() || isStaffNonAdvertiser) {
      res = this.strategyTypeList.filter(itm => itm.value === PunchCardStrategyType.Points)
    } else if (effectiveCaptureToolType !== PunchCardReceiptToolType.OCR
      && effectiveCaptureToolType !== PunchCardReceiptToolType.AdvertiserCampaign
      && effectiveCaptureToolType !== PunchCardReceiptToolType.SecretCode
      && effectiveCaptureToolType !== PunchCardReceiptToolType.UniqueQRCode) {
      res = this.strategyTypeList.filter(itm => itm.value === PunchCardStrategyType.Stamps)
    }
    return res;
  }

  form = new FormGroup({
    strategyType: new FormControl('', Validators.required),
    schemeList: new FormControl(''),
  });

  async ngOnChanges(simpleChanges: SimpleChanges) {
    const { campaign, strategyType, captureToolType } = simpleChanges;

    if (campaign?.currentValue) {
      this.strategy = campaign.currentValue.strategy;
      this.campaignId = campaign.currentValue.id;
    }

    if (captureToolType?.currentValue) {
      this.showIncentive = captureToolType.currentValue === PunchCardReceiptToolType.OCR
        || captureToolType.currentValue === PunchCardReceiptToolType.AdvertiserCampaign;
    }

    // Update validation when strategyType input changes
    if (strategyType?.currentValue !== undefined) {
      this.updateValidation();
    }
  }

  async ngAfterViewInit() {
    await this.setFormValue(this.strategy);
  }

  onSchemeChange(ev, scheme: PunchCardSchema) {
    scheme.name = ev?.name;
    this.updateValidation();
  }

  async ngOnInit() {
    if (this.strategy) {
      this.strategyType = this.strategy.strategyType;
    }
    this.updateValidation();
  }

  scrollToSchemeList() {
    const schemesEl = document.querySelector('#schemeList');
    if (schemesEl) {
      schemesEl.scrollIntoView({
        behavior: 'smooth'
      });
    }

  }

  updateValidation() {
    if (this.punchCardCampaignService.captureToolType === PunchCardReceiptToolType.UniqueSafebox || this.punchCardCampaignService.captureToolType === PunchCardReceiptToolType.HandshakeQR) {
      this._isValid = true;
      return true;
    }

    // For new campaigns, we need to check if strategyType is selected
    if (!this.strategyType) {
      this._isValid = false;
      this.form.get('strategyType')?.markAsTouched();
      return false;
    }

    let valid = this.checkAllSchemeValid();
    this._isValid = valid && (this.schemeList?.length > 0);

    // Mark form controls as touched to show validation errors
    if (!this._isValid) {
      this.form.get('strategyType')?.markAsTouched();
    }

    // Trigger change detection to update UI
    this.cdr.detectChanges();

    return this._isValid;
  }

  isValid() {
    return this._isValid;
  }

  isValidHandshakeQRScheme() {
    const schemes = this.getSchemeList();
    const scheme = schemes?.[0];

    const requiredFields = [
      { value: scheme?.periodDurationQrCode, message: 'QR Code Duration' },
      { value: scheme?.distanceAllowed, message: 'Distance Allowed' },
      { value: scheme?.senderPoints, message: 'QR Generator' },
      { value: scheme?.receiverPoints, message: 'QR Scanner' }
    ];

    for (const field of requiredFields) {
      if (field.value === undefined || field.value === null) {
        new MessageDialogModel(`The [${field.message}] must be defined for the HandshakeQR scheme.`).show(this.dialog);
        return false;
      }
    }
    return true;
  }

  checkAllSchemeValid() {
    let valid = true;
    this.schemeComponentList()?.forEach(scheme => {
      valid &&= scheme.isValid();
      scheme.forceValidatorsToDisplayErrors();
    });
    return valid;
  }

  addScheme() {

    if (this.punchCardCampaignService.captureToolType == 'UniqueSafebox') {
      return;
    }

    if (!this.schemeList) {
      this.schemeList = [];
    }

    if (!this.checkAllSchemeValid()) {
      return;
    }

    if (this.punchCardCampaignService.captureToolType == PunchCardReceiptToolType.UniqueQRCode
      && this.punchCardCampaignService.targetRole === CampaignTargetUser.Staff
      && this.schemeList.length > 0 && this.punchCardCampaignService.allowFlow == false) {
      new MessageDialogModel('A Unique QR campaign targeting the Staff role in a non-tier setup can have only one scheme.').show(this.dialog);
      return;
    }

    this.schemeList.unshift({
      id: this.generateSchemeId(),
      index: this.schemeList.length
    } as any);

    this.scrollToSchemeList();

    this.onSchemeChanged.emit(this.schemeList);
    this.updateValidation();
  }

  removeScheme(event, scheme: PunchCardSchema) {
    event.stopPropagation();
    if (this.punchCardCampaignService.isLinkedScheme(scheme)) {
      new MessageDialogModel('This scheme is already assigned to a tier(s). To remove this scheme, it first needs to be un-assigned from the tier(s)').show(this.dialog);

      return;
    }
    this.schemeList = this.schemeList.filter(s => s.id !== scheme.id);
    this.onSchemeChanged.emit(this.schemeList);
    this.updateValidation();

  }

  getSchemeList() {
    const schemes = [];
    this.schemeComponentList().forEach(scheme => {
      schemes.push(scheme.getFormValue());
    })
    return schemes;
  }



  async setFormValue(model: PunchCardStrategy) {
    if (!model)
      return;

    this.schemeList = model.schemes?.filter(s => s != null) ?? [];
    this.strategyType = model.strategyType;
    this.baseCalculation = model.calculationBaseType || this.baseCalculation || CalculationBasedType.QuantityBased;
    this.currencyCode = model.currencyCode || this.currencyCode || this.currencyList[0].value;

    this.updateJoinRewardWithStampCount(model);

    this.firstConsumptionReward()?.setFormValue(model.firstConsumptionReward);
    this.consumptionReward()?.setFormValue(model.consumptionReward);
    this.birthdayReward()?.setFormValue(model.birthdayReward);

    this.form.patchValue({
      strategyType: model.strategyType,
    });

    this.isValid();
  }

  getFormValue() {
    if (!this.isValid()) {
      return undefined;
    }

    const model = new PunchCardStrategy();
    model.init(({
      ...this.strategy,
      name: 'PunchCardObsolete'
    }));
    model.strategyType = this.strategyType;
    model.schemes = this.getSchemeList();
    model.timeLimit = new TimeLimitRule(model.timeLimit);
    model.consumptionLimit = new ConsumptionLimitRule(model.consumptionLimit);
    model.firstConsumptionReward = this.firstConsumptionReward().getFormValue();
    model.consumptionReward = this.consumptionReward().getFormValue();
    model.birthdayReward = this.birthdayReward().getFormValue();
    this.updateModelWithStampCountAndJoinReward(model);

    if (this.punchCardCampaignService.captureToolType == this.receiptToolTypes.OCR ||
      this.punchCardCampaignService.captureToolType == this.receiptToolTypes.AdvertiserCampaign
    ) {
      model.calculationBaseType = this.baseCalculation;
      model.currencyCode = this.currencyCode != "All Currencies" ? this.currencyCode : null;
    }

    return model;
  }

  forceValidatorsToDisplayErrors() {
    this.schemeComponentList()?.forEach(scheme => {

      scheme.forceValidatorsToDisplayErrors();
    });
  }

  async onSubmit() {
  }

  // the "Instant win for joining campaign" will display the initialStampCount in the Stamp Reward field
  updateJoinRewardWithStampCount(strategy: PunchCardStrategy) {
    if (!strategy) return;

    const { joinReward, initialStampCount } = strategy;
    const stampReward = initialStampCount !== 0
      ? new StampReward({ stamps: initialStampCount } as IStampReward)
      : null;
    const joinRewardData = new RewardData({
      ...(joinReward ?? {}),
      stampReward,
    });
    this.joinReward()?.setFormValue(joinRewardData);
  }

  // the "Instant win for joining campaign" will update the initialStampCount using the Stamp Reward field.
  updateModelWithStampCountAndJoinReward(model: PunchCardStrategy) {
    const joinReward = this.joinReward()?.getFormValue();
    model.initialStampCount = joinReward?.stampReward?.stamps || 0;

    if (joinReward) {
      joinReward.stampReward = null;
    }

    model.joinReward = joinReward;
  }

  onStrategyTypeChange(ev: any) {
    this.strategyType = ev.value;
    this.onStrategyTypeChanged.emit(ev.value);
    this.updateValidation();
  }

  onBaseCalculationChange(ev: any) {
    this.baseCalculation = ev.value;
    this.onCurrencyChange(this.currencyList[0]);
    this.onBaseCalculationChanged.emit(ev.value);
  }

  onCurrencyChange(ev: any) {
    this.currencyCode = ev?.value;
    this.onCurrencyChanged.emit(ev?.value);
  }

  displayDuration(duration: string) {
    return DateUtils.displayCsDuration(duration);
  }

  generateSchemeId() {
    return GUID.newGuid().replace(/-/g, '');
  }

  compareFn: ((a: any, b: any) => boolean) | null = (a: any, b: any) => a && b && a.id === b.id;
  compareRewardFn: ((a: CoinReward, b: CoinReward) => boolean) | null = (a: any, b: any) => a && b && a.coins === b.coins;
  compareProductRewardFn: ((a: ProductReward, b: ProductReward) => boolean) | null = (a: ProductReward, b: ProductReward) => a && b && a.productSlug === b.productSlug;
}


export const rewardRequiredValidator = (field1: any, field2: any): ValidatorFn => (control: AbstractControl) => {
  // if no reward is selected -> show error
  if (!control.get(field1).value && !control.get(field2).value)
    return { rewardRequired: { valid: false } };
  return null;
};
